/** Dataflow mode: wires up the graph manager, editor, and plot display. */

import { dataflow_codegen, dataflow_codegen_multi } from '../../pkg/rustsim.js';
import { $, $btn, $input } from '../dom.js';
import { DataflowManager } from './graph.js';
import { DataflowEditor } from './editor.js';
import { drawPlot } from './plot.js';
import { DEFAULT_CONFIGS, initDefaultConfigs } from './palette.js';
import { createZip } from './zip.js';
import { HilClient } from './hil-client.js';
import { renderPinTable } from './pin-table.js';
import { renderI2cPanel, updateI2cBuses } from './i2c-panel.js';
import {
  serializeProject, saveProject, loadProject, deleteProject,
  listProjects, getActiveProjectName, setActiveProjectName,
  uniqueName, createAutoSave,
} from './storage.js';
import { createSidebar } from './sidebar.js';
import { TelemetryPublisher } from './telemetry.js';
import type { GraphSnapshot, Value } from './types.js';

/** Unwrap a value that may be a plain number or a newtype wrapper {0: number}. */
function unwrapId(v: number | { 0: number }): number {
  return typeof v === 'number' ? v : v[0];
}

let mgr: DataflowManager | null = null;
let editor: DataflowEditor | null = null;
let hilClient: HilClient | null = null;
let activeProjectName = 'Untitled';
let triggerAutoSave: (() => void) | null = null;
let telemetry: TelemetryPublisher | null = null;

export function initDataflow(): void {
  // Initialize default configs from WASM function def schema
  try {
    const defs = DataflowManager.functionDefs();
    initDefaultConfigs(defs);
  } catch (_) {
    // Fall back to legacy hardcoded configs if WASM API not available
  }

  mgr = new DataflowManager(0.01);
  const container = $('dataflow-workspace') as HTMLDivElement;
  editor = new DataflowEditor(container, mgr);
  telemetry = new TelemetryPublisher();
  mgr.telemetry = telemetry;

  editor.onSelect = (blockId, snap) => {
    updateBlockInfo(blockId, snap);
  };

  editor.onEdgeSelect = (channelId, snap) => {
    updateEdgeInfo(channelId, snap);
  };

  // ── Project management ──────────────────────────────────────────
  const projectNameEl = $('df-project-name');

  function currentSave() {
    if (!mgr || !editor) return;
    const snap = mgr.snapshot();
    const viewport = { panX: 0, panY: 0, scale: 1 }; // TODO: expose from editor
    const project = serializeProject(activeProjectName, snap, mgr.positions, viewport);
    saveProject(project);
    setActiveProjectName(activeProjectName);
  }

  triggerAutoSave = createAutoSave(() => currentSave(), 1000);

  function updateProjectNameDisplay() {
    projectNameEl.textContent = activeProjectName;
  }

  function refreshSidebar() {
    const projects = listProjects();
    const infos = projects.map(name => {
      const p = loadProject(name);
      return { name, lastModified: p?.lastModified ?? '' };
    });
    sidebar.renderProjects(infos, activeProjectName);
  }

  function resetEditor(dt: number) {
    if (!mgr || !editor) return;
    mgr.stop();
    $btn('df-play').textContent = 'Play';
    editor.destroy();
    mgr.destroy();
    mgr = new DataflowManager(dt);
    if (telemetry) mgr.telemetry = telemetry;
    telemetry?.publish({ tag: 55 });
    editor = new DataflowEditor(container, mgr);
    editor.onSelect = (blockId, snap) => updateBlockInfo(blockId, snap);
    editor.onEdgeSelect = (channelId, snap) => updateEdgeInfo(channelId, snap);
    editor.onChange = () => triggerAutoSave?.();
    editor.resize();
  }

  function loadProjectByName(name: string) {
    const project = loadProject(name);
    if (!project) return;
    const dt = parseFloat($input('df-dt').value) || 0.01;
    resetEditor(dt);
    mgr!.restoreProject(project);
    activeProjectName = name;
    setActiveProjectName(name);
    updateProjectNameDisplay();
    editor!.updateSnapshot();
  }

  // Set up sidebar panel
  const sidebarContainer = $('df-sidebar-panel');
  const sidebar = createSidebar({
    onLoad: (name) => {
      currentSave();
      loadProjectByName(name);
      refreshSidebar();
    },
    onDelete: (name) => {
      deleteProject(name);
      if (name === activeProjectName) {
        const dt = parseFloat($input('df-dt').value) || 0.01;
        resetEditor(dt);
        activeProjectName = 'Untitled';
        setActiveProjectName(activeProjectName);
        updateProjectNameDisplay();
      }
      refreshSidebar();
    },
  });
  sidebarContainer.appendChild(sidebar.element);

  // Toolbar: New
  $btn('df-new').addEventListener('click', () => {
    currentSave();
    const dt = parseFloat($input('df-dt').value) || 0.01;
    resetEditor(dt);
    activeProjectName = uniqueName('Untitled', listProjects());
    setActiveProjectName(activeProjectName);
    updateProjectNameDisplay();
    refreshSidebar();
  });

  // Toolbar: Save As
  $btn('df-save-as').addEventListener('click', () => {
    const name = prompt('Project name:');
    if (!name || !name.trim()) return;
    const finalName = uniqueName(name.trim(), listProjects());
    activeProjectName = finalName;
    currentSave();
    updateProjectNameDisplay();
    refreshSidebar();
  });

  // Toolbar: Projects toggle
  $btn('df-projects').addEventListener('click', () => {
    refreshSidebar();
    sidebar.toggle();
  });

  // Auto-save on graph changes
  editor.onChange = () => triggerAutoSave?.();

  // Also auto-save on config apply (handled in updateBlockInfo)

  // Restore last active project on init
  const lastActive = getActiveProjectName();
  if (lastActive && loadProject(lastActive)) {
    loadProjectByName(lastActive);
  } else {
    activeProjectName = 'Untitled';
    updateProjectNameDisplay();
  }

  // Transport controls
  $btn('df-play').addEventListener('click', () => {
    if (!mgr) return;
    if (mgr.running) {
      mgr.stop();
      $btn('df-play').textContent = 'Play';
    } else {
      mgr.start();
      $btn('df-play').textContent = 'Pause';
    }
  });

  $btn('df-reset').addEventListener('click', () => {
    if (!mgr) return;
    const dt = parseFloat($input('df-dt').value) || 0.01;
    resetEditor(dt);
    triggerAutoSave?.();
  });

  $input('df-speed').addEventListener('input', () => {
    if (!mgr) return;
    mgr.setSpeed(parseFloat($input('df-speed').value) || 1.0);
  });

  $btn('df-batch').addEventListener('click', () => {
    if (!mgr) return;
    const steps = parseInt($input('df-batch-steps').value) || 100;
    const dt = parseFloat($input('df-dt').value) || 0.01;
    const snap = mgr.runBatch(steps, dt);
    updatePlots(snap);
    editor?.updateSnapshot();
  });

  // Export Rust crate (legacy single-target)
  $btn('df-export-rust').addEventListener('click', () => {
    if (!mgr) return;
    const statusEl = $('df-export-status');
    const dt = parseFloat($input('df-dt').value) || 0.01;
    try {
      // Check if multi-target checkboxes exist and any are checked
      const targetChecks = document.querySelectorAll<HTMLInputElement>('.df-target-check');
      if (targetChecks.length > 0) {
        const selectedTargets = Array.from(targetChecks)
          .filter(cb => cb.checked)
          .map(cb => ({
            target: cb.dataset.target!,
            binding: { target: cb.dataset.target!, pins: [] },
          }));
        if (selectedTargets.length === 0) {
          statusEl.textContent = 'Select at least one target.';
          statusEl.className = 'text-xs mt-2 min-h-4 text-danger';
          return;
        }
        const json = dataflow_codegen_multi(
          mgr.graphId,
          dt,
          JSON.stringify(selectedTargets),
        );
        const files: Array<[string, string]> = JSON.parse(json);
        downloadAsZip(files);
        statusEl.textContent = `Exported workspace: ${files.length} files.`;
        statusEl.className = 'text-xs mt-2 min-h-4 text-success';
      } else {
        const json = dataflow_codegen(mgr.graphId, dt);
        const files: Array<[string, string]> = JSON.parse(json);
        downloadAsZip(files);
        statusEl.textContent = `Exported ${files.length} files.`;
        statusEl.className = 'text-xs mt-2 min-h-4 text-success';
      }
    } catch (e) {
      statusEl.textContent = `Export error: ${e}`;
      statusEl.className = 'text-xs mt-2 min-h-4 text-danger';
    }
  });

  // Set up target selection checkboxes
  setupTargetCheckboxes();

  // Multi-board management
  setupBoardManager();

  // Sidebar block palette
  setupSidebarPalette();

  // HIL connection + tabs
  setupHilConnection();
  setupDfRightTabs();
}

export function resizeDataflow(): void {
  editor?.resize();
}

export function activateDataflow(): void {
  requestAnimationFrame(() => editor?.resize());
}

function updateBlockInfo(blockId: number | null, snap: GraphSnapshot | null): void {
  const infoEl = $('df-block-info');
  if (blockId === null || !snap) {
    // Safe: static content only
    infoEl.textContent = '';
    const span = document.createElement('span');
    span.className = 'text-text-dim';
    span.textContent = 'Select a block to view details';
    infoEl.appendChild(span);
    return;
  }
  const block = snap.blocks.find(b => b.id === blockId);
  if (!block) return;

  // Build info DOM safely
  infoEl.textContent = '';
  const nameEl = document.createElement('b');
  nameEl.textContent = block.name;
  infoEl.appendChild(nameEl);
  const idSpan = document.createElement('span');
  idSpan.className = 'text-text-dim';
  idSpan.textContent = ` #${block.id}`;
  infoEl.appendChild(idSpan);
  infoEl.appendChild(document.createElement('br'));
  const typeSpan = document.createElement('span');
  typeSpan.className = 'text-text-dim text-[11px]';
  typeSpan.textContent = block.block_type;
  infoEl.appendChild(typeSpan);
  infoEl.appendChild(document.createElement('br'));

  if (block.output_values.length > 0) {
    const valDiv = document.createElement('div');
    valDiv.className = 'mt-1.5 text-xs';
    for (let i = 0; i < block.outputs.length; i++) {
      const val = block.output_values[i];
      const row = document.createElement('div');
      row.textContent = `${block.outputs[i].name}: ${formatValue(val)}`;
      valDiv.appendChild(row);
    }
    infoEl.appendChild(valDiv);
  }

  // Config section
  const configKeys = Object.keys(block.config ?? {});
  if (configKeys.length > 0) {
    const configDiv = document.createElement('div');
    configDiv.className = 'mt-2 text-xs';
    const configLabel = document.createElement('b');
    configLabel.textContent = 'Config';
    configDiv.appendChild(configLabel);

    const inputs: Record<string, HTMLInputElement> = {};
    for (const key of configKeys) {
      const row = document.createElement('div');
      row.className = 'mt-1';
      const label = document.createElement('label');
      label.className = 'block text-text-dim text-[11px]';
      label.textContent = key;
      row.appendChild(label);
      const input = document.createElement('input');
      const val = block.config[key];
      if (typeof val === 'number') {
        input.type = 'number';
        input.step = 'any';
        input.value = String(val);
      } else {
        input.type = 'text';
        input.value = String(val ?? '');
      }
      input.className = 'w-full bg-bg border border-border rounded text-text text-xs px-2 py-1 mt-0.5 outline-none';
      row.appendChild(input);
      configDiv.appendChild(row);
      inputs[key] = input;
    }

    const applyBtn = document.createElement('button');
    applyBtn.className = 'btn btn-primary btn-sm mt-2';
    applyBtn.textContent = 'Apply';
    applyBtn.addEventListener('click', () => {
      if (!mgr || !editor) return;
      const newConfig: Record<string, unknown> = {};
      for (const key of configKeys) {
        const raw = inputs[key].value;
        const origVal = block.config[key];
        newConfig[key] = typeof origVal === 'number' ? parseFloat(raw) || 0 : raw;
      }
      mgr.updateBlock(blockId, block.block_type, newConfig);
      const newSnap = mgr.snapshot();
      editor.updateSnapshot();
      updateBlockInfo(blockId, newSnap);
    });
    configDiv.appendChild(applyBtn);
    infoEl.appendChild(configDiv);
  }

  // Delete block button
  const deleteBtn = document.createElement('button');
  deleteBtn.className = 'btn btn-sm mt-2';
  deleteBtn.style.backgroundColor = 'var(--color-danger)';
  deleteBtn.style.color = 'white';
  deleteBtn.textContent = 'Delete Block';
  deleteBtn.addEventListener('click', () => {
    if (!mgr || !editor) return;
    mgr.removeBlock(blockId);
    editor.clearSelection();
    editor.updateSnapshot();
    updateBlockInfo(null, null);
  });
  infoEl.appendChild(deleteBtn);

  // If it's a plot block, update the plot canvas
  updatePlots(snap);
}

function updateEdgeInfo(channelId: number | null, snap: GraphSnapshot | null): void {
  const infoEl = $('df-block-info');
  if (channelId === null || !snap) {
    infoEl.textContent = '';
    const span = document.createElement('span');
    span.className = 'text-text-dim';
    span.textContent = 'Select a block or edge to view details';
    infoEl.appendChild(span);
    return;
  }
  const ch = snap.channels.find(c => unwrapId(c.id) === channelId);
  if (!ch) return;

  const fromBlock = snap.blocks.find(b => b.id === unwrapId(ch.from_block));
  const toBlock = snap.blocks.find(b => b.id === unwrapId(ch.to_block));

  infoEl.textContent = '';
  const title = document.createElement('b');
  title.textContent = 'Channel';
  infoEl.appendChild(title);
  const idSpan = document.createElement('span');
  idSpan.className = 'text-text-dim';
  idSpan.textContent = ` #${channelId}`;
  infoEl.appendChild(idSpan);
  infoEl.appendChild(document.createElement('br'));

  const detailDiv = document.createElement('div');
  detailDiv.className = 'mt-1.5 text-xs';

  const fromName = fromBlock ? `${fromBlock.name}` : `Block ${unwrapId(ch.from_block)}`;
  const fromPortName = fromBlock?.outputs[ch.from_port]?.name ?? `port ${ch.from_port}`;
  const toName = toBlock ? `${toBlock.name}` : `Block ${unwrapId(ch.to_block)}`;
  const toPortName = toBlock?.inputs[ch.to_port]?.name ?? `port ${ch.to_port}`;

  const fromRow = document.createElement('div');
  fromRow.textContent = `From: ${fromName} → ${fromPortName}`;
  detailDiv.appendChild(fromRow);

  const toRow = document.createElement('div');
  toRow.textContent = `To: ${toName} → ${toPortName}`;
  detailDiv.appendChild(toRow);

  infoEl.appendChild(detailDiv);

  const disconnectBtn = document.createElement('button');
  disconnectBtn.className = 'btn btn-sm mt-2';
  disconnectBtn.style.backgroundColor = 'var(--color-danger)';
  disconnectBtn.style.color = 'white';
  disconnectBtn.textContent = 'Disconnect';
  disconnectBtn.addEventListener('click', () => {
    if (!mgr || !editor) return;
    mgr.disconnect(channelId);
    editor.clearSelection();
    editor.updateSnapshot();
    updateEdgeInfo(null, null);
  });
  infoEl.appendChild(disconnectBtn);
}

function formatValue(val: Value | null): string {
  if (!val) return '\u2014';
  switch (val.type) {
    case 'Float': return val.data.toFixed(4);
    case 'Text': return `"${val.data.slice(0, 30)}"`;
    case 'Bytes': return `[${val.data.length} bytes]`;
    case 'Series': return `[${val.data.length} samples]`;
    default: return String(val.data);
  }
}

// ── Sidebar block palette ─────────────────────────────────────────

function setupSidebarPalette(): void {
  const containerEl = document.getElementById('df-sidebar-palette');
  const filterInput = document.getElementById('df-palette-filter') as HTMLInputElement | null;
  if (!containerEl) return;
  const container = containerEl;

  const blockTypes = DataflowManager.blockTypes();

  function render(filter: string): void {
    container.textContent = '';
    const lower = filter.toLowerCase();
    let lastCat = '';

    for (const bt of blockTypes) {
      if (filter && !bt.name.toLowerCase().includes(lower) && !bt.block_type.toLowerCase().includes(lower)) {
        continue;
      }
      if (bt.category !== lastCat) {
        lastCat = bt.category;
        const header = document.createElement('div');
        header.className = 'text-[10px] text-text-dim uppercase tracking-wider px-2 pt-2 pb-1';
        if (container.childNodes.length > 0) {
          header.style.borderTop = '1px solid var(--color-border)';
        }
        header.textContent = bt.category;
        container.appendChild(header);
      }
      const item = document.createElement('button');
      item.className = 'block w-full text-left text-xs px-2 py-1.5 cursor-pointer bg-transparent border-none text-text transition-colors';
      item.style.cssText = 'border-left: 2px solid transparent;';
      item.addEventListener('mouseenter', () => {
        item.style.background = 'var(--color-border)';
        item.style.borderLeftColor = 'var(--color-accent)';
      });
      item.addEventListener('mouseleave', () => {
        item.style.background = 'transparent';
        item.style.borderLeftColor = 'transparent';
      });
      item.textContent = bt.name;
      item.addEventListener('click', () => {
        if (!mgr || !editor) return;
        const config = DEFAULT_CONFIGS[bt.block_type] ?? {};
        mgr.addBlock(bt.block_type, config, 200, 200);
        editor.updateSnapshot();
        editor.onChange?.();
      });
      container.appendChild(item);
    }
  }

  render('');
  filterInput?.addEventListener('input', () => render(filterInput.value));
}

// ── Multi-board management ─────────────────────────────────────────

const MCU_FAMILIES = [
  { id: 'Host', label: 'Host (Simulation)' },
  { id: 'Rp2040', label: 'RP2040 (Pico)' },
  { id: 'Stm32f4', label: 'STM32F4' },
  { id: 'Stm32g0b1', label: 'STM32G0B1' },
  { id: 'Esp32c3', label: 'ESP32-C3' },
] as const;

interface BoardDef {
  nodeId: string;
  mcuFamily: string;
}

/** Active boards in the current deployment profile. */
const deployBoards: BoardDef[] = [];
/** Block-id to node-id assignment. */
const blockAssignments: Map<number, string> = new Map();

function setupBoardManager(): void {
  const listEl = document.getElementById('df-board-list');
  const nameInput = document.getElementById('df-board-name') as HTMLInputElement | null;
  const mcuSelect = document.getElementById('df-board-mcu') as HTMLSelectElement | null;
  const addBtn = document.getElementById('df-board-add') as HTMLButtonElement | null;
  const assignEl = document.getElementById('df-block-assignments');

  if (!listEl || !nameInput || !mcuSelect || !addBtn || !assignEl) return;

  // Populate MCU family dropdown
  mcuSelect.textContent = '';
  for (const fam of MCU_FAMILIES) {
    const opt = document.createElement('option');
    opt.value = fam.id;
    opt.textContent = fam.label;
    mcuSelect.appendChild(opt);
  }

  // Add board button
  addBtn.addEventListener('click', () => {
    const nodeId = nameInput.value.trim();
    if (!nodeId) return;
    const mcuFamily = mcuSelect.value;
    if (deployBoards.some(b => b.nodeId === nodeId)) return; // no dupes
    deployBoards.push({ nodeId, mcuFamily });
    nameInput.value = '';
    renderBoardList();
    renderBlockAssignments();
  });

  function renderBoardList(): void {
    if (!listEl) return;
    listEl.textContent = '';
    if (deployBoards.length === 0) {
      const hint = document.createElement('span');
      hint.className = 'text-text-dim text-[11px]';
      hint.textContent = 'No boards added (single-board mode)';
      listEl.appendChild(hint);
      return;
    }
    for (let i = 0; i < deployBoards.length; i++) {
      const b = deployBoards[i];
      const row = document.createElement('div');
      row.className = 'flex items-center justify-between py-0.5';
      const info = document.createElement('span');
      info.textContent = `${b.nodeId} (${b.mcuFamily})`;
      row.appendChild(info);
      const removeBtn = document.createElement('button');
      removeBtn.textContent = '\u00d7';
      removeBtn.className = 'text-text-dim hover:text-danger text-sm leading-none ml-2 cursor-pointer';
      removeBtn.addEventListener('click', () => {
        deployBoards.splice(i, 1);
        // Remove assignments to this board
        for (const [blockId, nodeId] of blockAssignments) {
          if (nodeId === b.nodeId) blockAssignments.delete(blockId);
        }
        renderBoardList();
        renderBlockAssignments();
      });
      row.appendChild(removeBtn);
      listEl.appendChild(row);
    }
  }

  function renderBlockAssignments(): void {
    if (!assignEl || !mgr) return;
    assignEl.textContent = '';

    if (deployBoards.length === 0) return;

    const snap = mgr.snapshot();
    if (!snap || snap.blocks.length === 0) {
      const hint = document.createElement('span');
      hint.className = 'text-text-dim text-[11px]';
      hint.textContent = 'Add blocks to assign them to boards';
      assignEl.appendChild(hint);
      return;
    }

    const header = document.createElement('b');
    header.textContent = 'Block Assignments';
    header.className = 'text-xs block mb-1';
    assignEl.appendChild(header);

    for (const block of snap.blocks) {
      const row = document.createElement('div');
      row.className = 'flex items-center justify-between py-0.5 gap-1';

      const label = document.createElement('span');
      label.className = 'truncate flex-1 text-[11px]';
      label.textContent = `#${block.id} ${block.name}`;
      row.appendChild(label);

      const select = document.createElement('select');
      select.className = 'bg-bg border border-border text-text px-1 py-0.5 rounded text-[11px] focus:outline-none focus:border-accent';

      const noneOpt = document.createElement('option');
      noneOpt.value = '';
      noneOpt.textContent = '(none)';
      select.appendChild(noneOpt);

      for (const board of deployBoards) {
        const opt = document.createElement('option');
        opt.value = board.nodeId;
        opt.textContent = board.nodeId;
        if (blockAssignments.get(block.id) === board.nodeId) {
          opt.selected = true;
        }
        select.appendChild(opt);
      }

      select.addEventListener('change', () => {
        if (select.value) {
          blockAssignments.set(block.id, select.value);
        } else {
          blockAssignments.delete(block.id);
        }
      });

      row.appendChild(select);
      assignEl.appendChild(row);
    }
  }

  renderBoardList();

  // Re-render assignments when the graph changes
  if (editor) {
    const prevOnSelect = editor.onSelect;
    editor.onSelect = (blockId, snap) => {
      prevOnSelect?.(blockId, snap);
      renderBlockAssignments();
    };
  }
}

// ── Target selection ──────────────────────────────────────────────

const TARGET_OPTIONS = [
  { id: 'Host', label: 'Host (Simulation)', checked: true },
  { id: 'Rp2040', label: 'RP2040 (Pico)', checked: false },
  { id: 'Stm32f4', label: 'STM32F4', checked: false },
  { id: 'Esp32c3', label: 'ESP32-C3', checked: false },
] as const;

function setupTargetCheckboxes(): void {
  const container = document.getElementById('df-target-select');
  if (!container) return;

  container.textContent = '';
  const label = document.createElement('b');
  label.textContent = 'Targets';
  label.className = 'text-xs';
  container.appendChild(label);

  for (const target of TARGET_OPTIONS) {
    const row = document.createElement('label');
    row.className = 'flex items-center gap-1.5 text-xs mt-1 cursor-pointer';
    const cb = document.createElement('input');
    cb.type = 'checkbox';
    cb.checked = target.checked;
    cb.className = 'df-target-check';
    cb.dataset.target = target.id;
    row.appendChild(cb);
    const span = document.createElement('span');
    span.textContent = target.label;
    row.appendChild(span);
    container.appendChild(row);
  }
}

// ── HIL connection ─────────────────────────────────────────────────

function setupHilConnection(): void {
  const connectBtn = $btn('hil-connect');
  const statusEl = $('hil-status');
  const deployBtn = document.getElementById('hil-deploy') as HTMLButtonElement;
  const deployStatus = $('hil-deploy-status');
  const pinContainer = $('hil-pin-table');
  const i2cContainer = $('hil-i2c-panel');

  connectBtn.addEventListener('click', () => {
    if (hilClient?.connected) {
      hilClient.disconnect();
      return;
    }
    const url = $input('hil-ws-url').value.trim();
    if (!url) return;

    hilClient = new HilClient();

    hilClient.onConnect = () => {
      statusEl.textContent = 'Connected';
      statusEl.className = 'text-xs text-success mb-2';
      connectBtn.textContent = 'Disconnect';
      deployBtn.disabled = false;
      if (telemetry && hilClient?.socket) {
        telemetry.attach(hilClient.socket);
        telemetry.setEnabled(true);
      }
    };

    hilClient.onDisconnect = () => {
      statusEl.textContent = 'Disconnected';
      statusEl.className = 'text-xs text-text-dim mb-2';
      connectBtn.textContent = 'Connect';
      deployBtn.disabled = true;
      telemetry?.detach();
    };

    hilClient.onBusList = (buses) => {
      updateI2cBuses(i2cContainer, buses, hilClient!);
    };

    hilClient.onPinConfig = (pins) => {
      renderPinTable(pinContainer, pins, (_idx, _pin) => {
        // Send pin edit back to MCU (future: dedicated set-pin message)
        hilClient?.getPinConfig();
      });
    };

    hilClient.onError = (msg) => {
      deployStatus.textContent = `Error: ${msg}`;
      deployStatus.className = 'text-xs mt-1 min-h-4 text-danger';
    };

    hilClient.onDeployAck = () => {
      deployStatus.textContent = 'Deployed successfully.';
      deployStatus.className = 'text-xs mt-1 min-h-4 text-success';
    };

    // Render initial empty I2C panel
    renderI2cPanel(i2cContainer, hilClient);

    hilClient.connect(url);
    statusEl.textContent = 'Connecting…';
    statusEl.className = 'text-xs text-warning mb-2';
  });

  // Deploy button
  deployBtn.addEventListener('click', () => {
    if (!mgr || !hilClient?.connected) return;
    const dt = parseFloat($input('df-dt').value) || 0.01;

    // Get selected target
    const targetChecks = document.querySelectorAll<HTMLInputElement>('.df-target-check:checked');
    const target = targetChecks.length > 0 ? targetChecks[0].dataset.target! : 'Host';

    try {
      const snap = mgr.snapshot();
      const snapshotJson = JSON.stringify(snap);
      hilClient.deploy(snapshotJson, target, dt);
      deployStatus.textContent = 'Deploying…';
      deployStatus.className = 'text-xs mt-1 min-h-4 text-warning';
    } catch (e) {
      deployStatus.textContent = `Deploy error: ${e}`;
      deployStatus.className = 'text-xs mt-1 min-h-4 text-danger';
    }
  });
}

// ── Right-pane tab switching ───────────────────────────────────────

function setupDfRightTabs(): void {
  const tabs = document.querySelectorAll<HTMLButtonElement>('.df-right-tab');
  tabs.forEach(tab => {
    tab.addEventListener('click', () => {
      const targetId = tab.dataset.dftab;
      if (!targetId) return;

      // Deactivate all tabs and panes
      tabs.forEach(t => t.classList.remove('active'));
      document.querySelectorAll('.df-tab-content').forEach(p => p.classList.remove('active'));

      // Activate clicked
      tab.classList.add('active');
      document.getElementById(targetId)?.classList.add('active');
    });
  });
}

/** Download generated crate files as a ZIP archive. */
function downloadAsZip(files: Array<[string, string]>): void {
  const blob = createZip(files);
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = 'dataflow-generated.zip';
  a.click();
  URL.revokeObjectURL(a.href);
}

function updatePlots(snap: GraphSnapshot): void {
  const plotCanvas = document.getElementById('df-plot-canvas') as HTMLCanvasElement | null;
  if (!plotCanvas) return;

  // Find the first plot block with series data
  for (const block of snap.blocks) {
    if (block.block_type === 'plot') {
      const val = block.output_values[0];
      if (val && val.type === 'Series') {
        drawPlot(plotCanvas, val.data, `Plot #${block.id}`);
        return;
      }
    }
  }
}
